import { useRouter } from 'next/router';
import { useEffect } from 'react';

import createUrl from '@glass/common/modules/url/createUrl';
import pathToURL from '@glass/common/modules/url/pathToURL';
import removeEmptyKeys from '@glass/common/modules/utils/removeEmptyKeys';

type QueryParamStateOptions = {
  queryValueOverrides?: { [key: string]: string | null };
  action?: 'push' | 'replace';
  enabled?: boolean;
};

const useQueryParamState = (
  queryKey: string,
  queryValueInput?: string | null,
  { queryValueOverrides, action = 'replace', enabled = true }: QueryParamStateOptions = {},
) => {
  const {
    query: { [queryKey]: currentQueryValue },
    asPath,
    isReady,
    [action]: routerAction,
  } = useRouter();

  useEffect(() => {
    if (!isReady || !enabled) {
      return;
    }

    const queryValue =
      queryValueInput && typeof queryValueOverrides?.[queryValueInput] !== 'undefined'
        ? queryValueOverrides[queryValueInput]
        : queryValueInput;

    if ((queryValue || null) !== (currentQueryValue || null)) {
      const { url, query } = pathToURL(asPath, true);
      const path = createUrl({
        pathname: url.pathname,
        query: removeEmptyKeys({
          ...query,
          [queryKey]: queryValue,
        }),
      });

      routerAction(path, path, { shallow: true, scroll: false });
    }
  }, [
    queryKey,
    queryValueInput,
    currentQueryValue,
    asPath,
    routerAction,
    queryValueOverrides,
    isReady,
    enabled,
  ]);
};

export default useQueryParamState;
